import AutorenewIcon from "@mui/icons-material/Autorenew";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShareIcon from "@mui/icons-material/Share";
import { Box, Typography, Button } from "@mui/material";
import React from "react";

import { MediaItem } from "./PreviewPane";

// A custom verified icon (used for Twitter) implemented as an inline SVG
const VerifiedIcon: React.FC = () => (
  <Box
    component="svg"
    sx={{ width: 16, height: 16, ml: 0.5, color: "#1DA1F2" }}
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.437.695.21 1.04z" />
  </Box>
);

interface TwitterPreviewProps {
  content: string;
  media: MediaItem[];
}

export const TwitterPreview: React.FC<TwitterPreviewProps> = ({ content, media }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
        {/* Profile placeholder */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "grey.300",
            flexShrink: 0, // Prevent avatar from shrinking
          }}
        />
        <Box sx={{ ml: 2, flex: 1, width: "100%", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mr: 0.5 }}>
              Your Account
            </Typography>
            <VerifiedIcon />
            <Typography
              variant="body2"
              sx={{
                ml: 0.5,
                color: "grey.500",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              @youraccount
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              overflowWrap: "break-word",
              wordWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap", // This preserves line breaks in content
            }}
          >
            {content || <span style={{ color: "grey" }}>Your tweet will appear here...</span>}
          </Typography>
          {media.length > 0 && (
            <Box
              sx={{
                mt: 2,
                display: "grid",
                gridTemplateColumns: media.length === 1 ? "1fr" : "1fr 1fr",
                gap: 2,
              }}
            >
              {media.map((item) => (
                <Box
                  key={item.id}
                  component="img"
                  src={item.url}
                  alt=""
                  sx={{
                    width: "100%",
                    height: 192,
                    objectFit: "cover",
                    borderRadius: 2,
                  }}
                />
              ))}
            </Box>
          )}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              color: "grey.500",
              flexWrap: "wrap", // Allow buttons to wrap if needed
            }}
          >
            <Button
              sx={{
                textTransform: "none",
                color: "inherit",
                "&:hover": { color: "blue" },
                minWidth: "auto", // Reduce minimum width
                px: 1, // Reduce padding
              }}
              startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
            >
              0
            </Button>
            <Button
              sx={{
                textTransform: "none",
                color: "inherit",
                "&:hover": { color: "green" },
                minWidth: "auto",
                px: 1,
              }}
              startIcon={<AutorenewIcon fontSize="small" />}
            >
              0
            </Button>
            <Button
              sx={{
                textTransform: "none",
                color: "inherit",
                "&:hover": { color: "pink" },
                minWidth: "auto",
                px: 1,
              }}
              startIcon={<FavoriteBorderIcon fontSize="small" />}
            >
              0
            </Button>
            <Button
              sx={{
                textTransform: "none",
                color: "inherit",
                "&:hover": { color: "blue" },
                minWidth: "auto",
                px: 1,
              }}
              startIcon={<ShareIcon fontSize="small" />}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
